/**
 * Alias tra chiave registry (modules_registry.key) e nome storico in tenant_modules.
 * Es. registry "edilizia" ↔ DB "vertical_edilizia".
 */

const MODULE_KEY_ALIASES: Record<string, string[]> = {
  edilizia: ["edilizia", "vertical_edilizia"],
  vertical_edilizia: ["edilizia", "vertical_edilizia"],
  billing: ["billing", "fatturazione"],
  fatturazione: ["billing", "fatturazione"],
};

/** Bundle CRM: chiave unica storica oppure i 4 sottomoduli registry. */
export const CRM_BUNDLE_KEY = "crm";
export const CRM_SUBMODULE_KEYS = ["companies", "contacts", "pipelines", "activities"] as const;
export const CRM_ALL_KEYS = [CRM_BUNDLE_KEY, ...CRM_SUBMODULE_KEYS] as const;

/** Tutte le chiavi equivalenti (include la chiave stessa). */
export function resolveModuleKeys(moduleKey: string): string[] {
  return MODULE_KEY_ALIASES[moduleKey] ?? [moduleKey];
}

type TenantModuleLike = {
  module_name: string;
  is_enabled?: boolean | null;
};

/** True se almeno un alias è abilitato sul tenant. */
export function isTenantModuleEnabled(
  modules: TenantModuleLike[] | null | undefined,
  moduleKey: string,
): boolean {
  if (!modules?.length) return false;
  const keys = resolveModuleKeys(moduleKey);
  return modules.some((m) => keys.includes(m.module_name) && !!m.is_enabled);
}

/**
 * CRM attivo se esiste la chiave bundle `crm`, oppure tutti i 4 sottomoduli.
 * Evita lo stato "Parziale" quando in DB c'è solo `crm = true`.
 */
export function isCrmBundleEnabled(
  modules: TenantModuleLike[] | null | undefined,
): boolean {
  if (!modules?.length) return false;
  if (isTenantModuleEnabled(modules, CRM_BUNDLE_KEY)) return true;
  return CRM_SUBMODULE_KEYS.every((key) => isTenantModuleEnabled(modules, key));
}

export function isCrmBundlePartial(
  modules: TenantModuleLike[] | null | undefined,
): boolean {
  if (isCrmBundleEnabled(modules)) return false;
  return CRM_SUBMODULE_KEYS.some((key) => isTenantModuleEnabled(modules, key));
}

/** Righe tenant_modules che corrispondono alla chiave (o alias). */
export function findTenantModulesByKey<T extends TenantModuleLike>(
  modules: T[] | null | undefined,
  moduleKey: string,
): T[] {
  if (!modules?.length) return [];
  const keys = resolveModuleKeys(moduleKey);
  return modules.filter((m) => keys.includes(m.module_name));
}

/**
 * Preferisce match esatto sulla chiave registry, altrimenti il primo alias.
 * Utile per update (evita di creare un secondo record).
 */
export function findPreferredTenantModule<T extends TenantModuleLike>(
  modules: T[] | null | undefined,
  moduleKey: string,
): T | null {
  if (!modules?.length) return null;
  const exact = modules.find((m) => m.module_name === moduleKey);
  if (exact) return exact;
  const keys = resolveModuleKeys(moduleKey);
  return modules.find((m) => keys.includes(m.module_name)) ?? null;
}
